import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useData } from "../backend/firebase";
import { StreamEmbed } from '../components/StreamEmbed';
import SEO from '../components/SEO';
import { getImagePath } from '../utils/imageRegistry';
import { useWindowSize } from '../hooks/useWindowSize';



const CustomPage = () => {
  const { slug } = useParams();
  const { customPages, loading, teams } = useData();
  const scrollContainerRef = useRef(null);
  const { isMobile } = useWindowSize();


  if (loading) return null;

  const page = customPages.find(p => p.slug === slug);

  if (!page) return (
    <div className="PageLayout">
        <div className="ContentWrapper">
            <div className="GlassCard" style={{textAlign:'center', padding: 50}}>
                <h2>Page Not Found</h2>
                <p>The page "{slug}" does not exist.</p>
            </div>
        </div>
    </div>
  );

  const sections = page.sections || (page.content ? [{id: 'legacy', type: 'card', content: page.content, visible: true}] : []);

  // --- 1. PRE-SCAN FOR TEAM HEADERS ---
  const tocItems = sections.reduce((acc, section) => {
    if (section.type === 'card' && section.visible !== false) {
      const match = section.content.match(/\[\[TEAM_HEADER:(.*?)]\]/);
      if (match) {
        const teamName = match[1].trim();
        const teamData = teams.find(t => t.name && t.name.toLowerCase() === teamName.toLowerCase());
        if (teamData) {
          acc.push({ sectionId: section.id, team: teamData });
        }
      }
    }
    return acc;
  }, []);

  const scrollToSection = (id) => {
      const element = document.getElementById(`section-${id}`);
      if (!element) return;

      if (isMobile) {
          // Now that the whole page scrolls naturally, we scroll the Window!
          // 140 offsets the height of the main sticky Navbar + the TOC bar
          const y = element.getBoundingClientRect().top + window.scrollY - 140;
          window.scrollTo({ top: y, behavior: 'smooth' });
      } else if (scrollContainerRef.current) {
          const topPos = element.offsetTop;
          scrollContainerRef.current.scrollTo({
              top: topPos - 20,
              behavior: 'smooth'
          });
      }
  };
  

  return (
    <div className="PageLayout" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
        <SEO 
            title={page.title} 
            description={`Read more about ${page.title} for Bridge Brawl 2026.`}
            url={`/page/${page.slug}`} 
        />
        
        {/* --- 2. TOP/SIDE NAVIGATION BAR --- */}
        {tocItems.length > 0 && (
            <div className="CustomScroll" style={{
                // DESKTOP: Fixed 70px sidebar | MOBILE: Auto-height top bar
                width: isMobile ? '100%' : 70,
                height: isMobile ? 'auto' : '100vh',
                minHeight: isMobile ? '70px' : 'auto',
                flexDirection: isMobile ? 'row' : 'column',
                
                // SHARED STYLES
                background: 'rgba(0,0,0,0.3)',
                borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.05)',
                borderBottom: isMobile ? '1px solid rgba(255,255,255,0.15)' : 'none',
                display: 'flex',
                alignItems: 'center',
                padding: isMobile ? '10px 15px' : '100px 0 40px 0',
                gap: 15,
                zIndex: 20,
                overflowX: isMobile ? 'auto' : 'hidden',
                overflowY: isMobile ? 'visible' : 'auto',
                WebkitOverflowScrolling: 'touch',
                flexShrink: 0, // Prevents the bar itself from being squished
                // position: isMobile ? 'relative' : 'fixed', // Fixed on desktop to not eat space
                position: isMobile ? 'sticky' : 'fixed',
                top: isMobile ? '60px' : 0, // Docks beautifully right under the main header!
                zIndex: 15,
                // left: 0,
                // top: 0,
            }}>
                {tocItems.map((item) => (
                    <button 
                        key={item.sectionId}
                        onClick={() => scrollToSection(item.sectionId)}
                        style={{
                            width: 44, height: 44, borderRadius: '50%', padding: 3,
                            cursor: 'pointer', background: 'rgba(255,255,255,0.05)',
                            border: `2px solid ${item.team.color}`,
                            flexShrink: 0, overflow: 'hidden'
                        }}
                    >
                        <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: '#222' }}>
                            {item.team.logoUrl ? (
                                <img src={getImagePath(item.team.logoUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : item.team.logo ? (
                                <img src={`data:image/svg+xml;utf8,${encodeURIComponent(item.team.logo)}`} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                            ) : (
                                <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize: 14, color:'#fff'}}>{item.team.name.charAt(0)}</div>
                            )}
                        </div>
                    </button>
                ))}
            </div>
        )}

        {/* --- 3. MAIN CONTENT AREA --- */}
        <div ref={scrollContainerRef} className="ContentWrapper CustomScroll" style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // Adjusted padding for mobile to clear the top bar and shrink gaps
            padding: isMobile ? '20px 15px 100px 15px' : '60px 20px',
            gap: 25,
            scrollBehavior: 'smooth'
        }}>
            
            {/* Title Card - Now smaller on mobile */}
            <div className="GlassCard" style={{ maxWidth: 900, width: '100%', padding: isMobile ? '10px' : '20px', flexShrink: 0 }}>
                <h1 className="SectionTitle" style={{ margin: 0, textAlign: 'center', fontSize: isMobile ? '20px' : '28px' }}>{page.title}</h1>
            </div>

            {sections.map(section => {
                if (section.visible === false) return null;

                if (section.type === 'card') {
                    const teamMacroRegex = /\[\[TEAM_HEADER:(.*?)]\]/;
                    const match = section.content.match(teamMacroRegex);
                    
                    let teamData = null;
                    let cleanContent = section.content;

                    if (match) {
                        const inputName = match[1].trim().toLowerCase();
                        teamData = teams.find(t => t.name && t.name.toLowerCase() === inputName);
                        cleanContent = section.content.replace(teamMacroRegex, '');
                    }

                    return (
                        <div 
                            id={`section-${section.id}`} 
                            key={section.id} 
                            className="GlassCard" 
                            style={{ 
                                maxWidth: 900, 
                                width: '100%', 
                                flexShrink: 0,
                                overflow: 'hidden',
                                borderTop: teamData ? `4px solid ${teamData.color}` : '1px solid rgba(255,255,255,0.15)',
                                scrollMarginTop: isMobile ? '90px' : '20px'
                            }}
                        >
                            {teamData && (
                                <div style={{ 
                                    background: 'rgba(0,0,0,0.3)', 
                                    padding: '15px 40px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 15,
                                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                                }}>
                                    <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', background: teamData.color || '#333' }}>
                                        {teamData.logoUrl ? (
                                            <img src={getImagePath(teamData.logoUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : teamData.logo ? (
                                            <img src={`data:image/svg+xml;utf8,${encodeURIComponent(teamData.logo)}`} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                                        ) : null}
                                    </div>
                                    <span style={{ fontWeight: 'bold', color: '#fff', fontSize: 14, letterSpacing: 0.5 }}>
                                        {teamData.name}
                                    </span>
                                </div>
                            )}

                            <div className="RichTextContent" style={{ padding: '5px 40px', lineHeight: 1.6, color: '#E0E0E0', fontSize: 16 }}>
                                <div dangerouslySetInnerHTML={{ __html: cleanContent }} />
                            </div>
                        </div>
                    );
                }
                
                if (section.type === 'media') {
                    const isVideo = section.content.includes('youtube') || section.content.includes('twitch') || section.content.includes('youtu.be');
                    return (
                        <div key={section.id} id={`section-${section.id}`} className="StandaloneMedia" style={{ width: '100%', maxWidth: 900, flexShrink: 0, display: 'flex', justifyContent: 'center', scrollMarginTop: '100px' }}>
                            {isVideo ? (
                                <div style={{ width: '100%', height: 500, background:'black', borderRadius: 16, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                                    <StreamEmbed link={section.content} />
                                </div>
                            ) : (
                                <img src={section.content} alt="" style={{ maxWidth: '100%', borderRadius: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }} />
                            )}
                        </div>
                    );
                }
                return null;
            })}

            {sections.length === 0 && (
                <div style={{ textAlign:'center', opacity: 0.5 }}>This page is empty.</div>
            )}
        </div>
    </div>
  );
};

export default CustomPage;